import { useCallback, useEffect, useRef, useState } from 'react';
import type { ConstructionPriceEstimatorTourHandle } from '../components/tour/ConstructionPriceEstimatorTour';
import {
  getConstructionEstimate, getConstructionEstimateJob,
  getConstructionDavisBaconScheduleDetails,
  getConstructionPriceSearchPrefillJob, getConstructionLaborPlanJob,
  getConstructionLaborPlanPreview,
  type EstimateDraft, type EstimateJob, type EstimateLoadData,
  type EstimateRevision, type DavisBaconScheduleDetails,
  type PriceSearchPrefillJob, type StandaloneProjectBrief,
  type ConstructionLaborPlanJob, type ConstructionLaborPlanPreview,
  type ConstructionLaborPlanProposal, type SourceMatchReviewDecision,
} from '../api/constructionPriceEstimatorFunctions';
import { useFlow } from '../components/StageWrapper';
import { normalizeApproximateSettings, type SiteStaffingRoleSelection } from '../api/approximateCostPlan';
import { readRerunReminder, rerunImpact, rerunReminderMessage,
  type RerunReminder } from './ConstructionPriceEstimatorWorkflow';
import { davisBaconCalculationFailureMessage, hasTerminalWageBackedRevision,
  isFailedDavisBaconCalculation } from './ConstructionPriceEstimatorWageState';
import { constructionResultPermissions } from './ConstructionPriceResultPermissions';
import { safeActionError } from './ConstructionPricePublicProjection';
import { requestConstructionSourceMatchProposal } from './ConstructionPriceSourceMatch';
import { ACTIVE_JOB_STATUSES, draftContent, emptyDraft,
  mergeUnsavedDraftFields, partialPrefillDraft } from './ConstructionPriceDraftState';
import { costDataAvailabilityMessage, costDataPrimaryConfigured,
  costDataPrimaryStatus } from './ConstructionPriceSupportingEvidence';

export function useConstructionPriceEstimatorState(flowId: string, pollIntervalMs: number) {
  const { flowName, isViewOnly, isSampleFlow, readOnlyReason } = useFlow();
  const tourRef = useRef<ConstructionPriceEstimatorTourHandle>(null);
  const [load, setLoad] = useState<EstimateLoadData | null>(null);
  const [projectBriefText, setProjectBriefText] = useState('');
  const [scopeDocumentBusy, setScopeDocumentBusy] = useState(false);
  const [laborPlanPreview, setLaborPlanPreview] = useState<ConstructionLaborPlanPreview | null>(null);
  const [laborPlanProposal, setLaborPlanProposal] = useState<ConstructionLaborPlanProposal | null>(null);
  const [laborPlanJob, setLaborPlanJob] = useState<ConstructionLaborPlanJob | null>(null);
  const [laborPlanReviewRoles, setLaborPlanReviewRoles] = useState<SiteStaffingRoleSelection[] | null>(null);
  const [useSavedLaborPlan, setUseSavedLaborPlan] = useState(true);
  const [startingLaborPlan, setStartingLaborPlan] = useState(false);
  const [applyingLaborPlan, setApplyingLaborPlan] = useState(false);
  const [laborPlanPollAttempt, setLaborPlanPollAttempt] = useState(0);
  const [wagePollAttempt, setWagePollAttempt] = useState(0);
  const [hasIncompleteCostComponentInputs, setHasIncompleteCostComponentInputs] = useState(false);
  const [rerunReminder, setRerunReminder] = useState<RerunReminder | null>(() => readRerunReminder(flowId));
  const [rerunToast, setRerunToast] = useState<string | null>(null);
  const [draft, setDraft] = useState<EstimateDraft>(emptyDraft);
  const [revision, setRevision] = useState<EstimateRevision | null>(null);
  const [job, setJob] = useState<EstimateJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [preparingPrefill, setPreparingPrefill] = useState(false);
  const [prefillJob, setPrefillJob] = useState<PriceSearchPrefillJob | null>(null);
  const [prefillPollAttempt, setPrefillPollAttempt] = useState(0);
  const [cancellingPrefill, setCancellingPrefill] = useState(false);
  const [davisBaconDetails, setDavisBaconDetails] = useState<DavisBaconScheduleDetails | null>(null);
  const [loadingDavisBaconDetails, setLoadingDavisBaconDetails] = useState(false);
  const [davisBaconDetailsReload, setDavisBaconDetailsReload] = useState(0);
  const [davisBaconReviewReason, setDavisBaconReviewReason] = useState('');
  const [submittingDavisBaconReview, setSubmittingDavisBaconReview] = useState(false);
  const [useSavedContractDetails, setUseSavedContractDetails] = useState(true);
  const [useSavedSourceResults, setUseSavedSourceResults] = useState(true);
  const [sourceMatchDecisions, setSourceMatchDecisions] = useState<Record<string, SourceMatchReviewDecision['decision']>>({});
  const [reviewingSourceMatches, setReviewingSourceMatches] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [currentResultsState, setCurrentResultsState] = useState<'idle' | 'running' | 'failed' | 'cancelled' | 'unavailable'>('idle');
  const [calculationRunInProgress, setCalculationRunInProgress] = useState(false);
  const [calculationStage, setCalculationStage] = useState<'saving' | 'searching' | 'preparing' | 'reconnecting'>('searching');
  const [pollAttempt, setPollAttempt] = useState(0);
  const [finalizing, setFinalizing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [romAcknowledged, setRomAcknowledged] = useState(false);
  const [acknowledgedKeys, setAcknowledgedKeys] = useState<Set<string>>(new Set());
  const acknowledgementRevisionRef = useRef<string | null>(null);
  const [comparableRationales, setComparableRationales] = useState<Record<string, string>>({});
  const [cancelling, setCancelling] = useState(false);
  const downloadInProgressRef = useRef(false);
  const resultRef = useRef<EstimateRevision | null>(null);
  const rerunReminderRef = useRef<RerunReminder | null>(rerunReminder);
  const draftRef = useRef<EstimateDraft>(emptyDraft());
  const acceptedDraftRef = useRef<EstimateDraft | null>(null);
  const partialDraftBaselineRef = useRef<EstimateDraft | null>(null);
  const acceptedPayloadRef = useRef('');
  const editSequenceRef = useRef(0);
  const acceptedSaveSequenceRef = useRef(0);
  const pendingSaveCountRef = useRef(0);
  const saveQueueRef = useRef<Promise<EstimateDraft | null>>(Promise.resolve(null));
  const saveFailedRef = useRef(false);
  const calculationStartedAtRef = useRef(0);
  const calculationRunInProgressRef = useRef(false);
  const calculationHasTerminalWageEvidenceRef = useRef(false);
  // The preparation action can remove its focused CTA. That blur is not a
  // user edit and must never race an empty client draft ahead of the server
  // proposal.
  const prefillPendingRef = useRef(false);
  // A proposal is tied to one accepted Contract Details snapshot. Keep a
  // client-side fence so polling/re-renders cannot repeatedly request one.
  const sourceMatchProposalFenceRef = useRef<string | null>(null);
  const savedProjectBriefRef = useRef<StandaloneProjectBrief>({ text: '', revision: 0 });
  const projectBriefTextRef = useRef('');
  const flowGenerationRef = useRef(0);
  const activeFlowIdRef = useRef(flowId);
  const reloadSequenceRef = useRef(0);
  const resultRequestGenerationRef = useRef(0);
  const prefillRequestGenerationRef = useRef(0);
  const laborRequestGenerationRef = useRef(0);
  const scopeRequestGenerationRef = useRef(0);
  if (activeFlowIdRef.current !== flowId) {
    activeFlowIdRef.current = flowId;
    flowGenerationRef.current += 1;
    reloadSequenceRef.current += 1;
    resultRequestGenerationRef.current += 1;
    prefillRequestGenerationRef.current += 1;
    calculationRunInProgressRef.current = false;
    calculationHasTerminalWageEvidenceRef.current = false;
    laborRequestGenerationRef.current += 1;
    scopeRequestGenerationRef.current += 1;
    savedProjectBriefRef.current = { text: '', revision: 0 };
    projectBriefTextRef.current = '';
    draftRef.current = emptyDraft();
    acceptedDraftRef.current = null;
    partialDraftBaselineRef.current = null;
    acceptedPayloadRef.current = '';
    editSequenceRef.current = 0;
    acceptedSaveSequenceRef.current = 0;
    pendingSaveCountRef.current = 0;
    saveQueueRef.current = Promise.resolve(null);
    saveFailedRef.current = false;
    prefillPendingRef.current = false;
    sourceMatchProposalFenceRef.current = null;
  }
  const captureFlowFence = () => {
    const generation = flowGenerationRef.current;
    return () => activeFlowIdRef.current === flowId && generation === flowGenerationRef.current;
  };

  useEffect(() => () => {
    flowGenerationRef.current += 1;
    resultRequestGenerationRef.current += 1;
    prefillRequestGenerationRef.current += 1;
    laborRequestGenerationRef.current += 1;
    scopeRequestGenerationRef.current += 1;
  }, []);

  const publishRerunReminder = useCallback((next: RerunReminder | null) => {
    rerunReminderRef.current = next;
    setRerunReminder(next);
    try {
      const key = `construction-price-estimator:${flowId}:rerun-reminder-v1`;
      if (next) window.localStorage.setItem(key, JSON.stringify(next));
      else window.localStorage.removeItem(key);
    } catch (_caught) { /* The in-memory reminder remains visible. */ }
  }, [flowId]);

  const applyLoad = useCallback((
    next: EstimateLoadData, preserveLocalDraft = false, acceptCompletedPrefillDraft = false,
  ) => {
    const incomingRevision = next.active_revision?.revision;
    const reminder = rerunReminderRef.current;
    if (typeof incomingRevision === 'number' && reminder) {
      const currentSettings = normalizeApproximateSettings(next.draft?.approximate_cost_plan_settings);
      const currentLaborPlan = currentSettings?.labor_plan_provenance ?? null;
      const hasCurrentComponents = Boolean(currentSettings && (
        currentSettings.material_components.length || currentSettings.assembly_components.length
        || currentSettings.project_work_requirements.length));
      const laborPlanWasReapplied = Boolean(currentLaborPlan) && reminder.level === 'materials'
        && reminder.labor_plan_provenance !== undefined
        && currentLaborPlan !== reminder.labor_plan_provenance
        && next.labor_plan_proposal?.status === 'applied'
        && next.labor_plan_proposal.proposal_id === currentLaborPlan;
      if (incomingRevision > reminder.revision) {
        publishRerunReminder(reminder.level === 'materials' && hasCurrentComponents && !laborPlanWasReapplied
          ? { ...reminder, revision: incomingRevision } : null);
      } else if (laborPlanWasReapplied) {
        publishRerunReminder({ revision: incomingRevision, level: 'prices' });
      }
    }
    const terminalWageBackedRevision = hasTerminalWageBackedRevision(next);
    if (terminalWageBackedRevision) calculationHasTerminalWageEvidenceRef.current = true;
    const failedWageProjection = calculationRunInProgressRef.current
      && isFailedDavisBaconCalculation(next.davis_bacon_schedule_job);
    if (failedWageProjection && calculationHasTerminalWageEvidenceRef.current
      && !terminalWageBackedRevision) return;
    resultRef.current = next.active_revision ?? null;
    const nextPartialDraft = next.draft ? null : partialPrefillDraft(next.prefill);
    const nextDraft = next.draft ?? nextPartialDraft ?? emptyDraft();
    let presentedDraft = draftRef.current;
    if (preserveLocalDraft && acceptCompletedPrefillDraft && nextPartialDraft
      && pendingSaveCountRef.current === 0) {
      presentedDraft = mergeUnsavedDraftFields(
        nextPartialDraft, partialDraftBaselineRef.current ?? emptyDraft(), draftRef.current,
      );
      draftRef.current = presentedDraft;
      setDraft(presentedDraft);
      partialDraftBaselineRef.current = nextPartialDraft;
      acceptedPayloadRef.current = draftContent(nextPartialDraft);
      saveQueueRef.current = Promise.resolve(nextPartialDraft);
      acceptedSaveSequenceRef.current += 1;
      saveFailedRef.current = false;
    }
    if (preserveLocalDraft && acceptCompletedPrefillDraft && next.draft
      && pendingSaveCountRef.current === 0
      && next.draft.draft_revision > (acceptedDraftRef.current?.draft_revision ?? 0)) {
      presentedDraft = mergeUnsavedDraftFields(
        next.draft, acceptedDraftRef.current ?? partialDraftBaselineRef.current ?? emptyDraft(), draftRef.current,
      );
      draftRef.current = presentedDraft;
      setDraft(presentedDraft);
      acceptedDraftRef.current = next.draft;
      partialDraftBaselineRef.current = null;
      acceptedPayloadRef.current = draftContent(next.draft);
      acceptedSaveSequenceRef.current += 1;
      saveQueueRef.current = Promise.resolve(next.draft);
      saveFailedRef.current = false;
    }
    // A background reload can finish after the owner edits Contract Details.
    // Keep its server-owned job/results projection, but do not replace the
    // newer editor snapshot or break an autosave already waiting in the queue.
    setLoad(preserveLocalDraft ? { ...next, draft: next.draft ? presentedDraft : null } : next);
    const nextBrief = next.project_brief ?? { text: '', revision: 0 };
    const preserveLocalBrief = projectBriefTextRef.current !== savedProjectBriefRef.current.text;
    savedProjectBriefRef.current = nextBrief;
    if (!preserveLocalBrief) {
      projectBriefTextRef.current = nextBrief.text;
      setProjectBriefText(nextBrief.text);
    }
    if (!preserveLocalDraft) {
      setDraft(nextDraft);
      draftRef.current = nextDraft;
      acceptedDraftRef.current = next.draft ?? null;
      partialDraftBaselineRef.current = nextPartialDraft;
      acceptedPayloadRef.current = next.draft || nextPartialDraft ? draftContent(nextDraft) : '';
      saveQueueRef.current = Promise.resolve(next.draft ?? nextPartialDraft ?? null);
      saveFailedRef.current = false;
    }
    const acknowledgementRevision = next.active_revision
      ? `${next.active_revision.estimate_id}:${next.active_revision.revision}` : null;
    if (acknowledgementRevision !== acknowledgementRevisionRef.current) {
      acknowledgementRevisionRef.current = acknowledgementRevision;
      setAcknowledgedKeys(new Set());
      setRomAcknowledged(false);
    }
    setRevision(next.active_revision ?? null);
    setJob(next.job ?? null);
    const nextPrefillJob = next.prefill_job ?? null;
    setPrefillJob(nextPrefillJob);
    setLaborPlanProposal(next.labor_plan_proposal ?? null);
    setLaborPlanJob(next.labor_plan_job ?? null);
    const prefillIsActive = Boolean(nextPrefillJob && ['pending', 'running'].includes(nextPrefillJob.status));
    prefillPendingRef.current = prefillIsActive;
    setPreparingPrefill(prefillIsActive);
    const activeDependentWageLookup = calculationRunInProgressRef.current
      && Boolean(next.davis_bacon_schedule_job
        && ACTIVE_JOB_STATUSES.has(next.davis_bacon_schedule_job.status));
    if ((next.job && ACTIVE_JOB_STATUSES.has(next.job.status)) || activeDependentWageLookup) {
      setCurrentResultsState('running');
      setCalculationStage('searching');
    }
    else if (next.job && ['canceled', 'cancelled'].includes(next.job.status)) setCurrentResultsState('cancelled');
    else if (next.job && ['failed', 'stale', 'blocked'].includes(next.job.status)) setCurrentResultsState('failed');
    else if (failedWageProjection && !terminalWageBackedRevision
      && isFailedDavisBaconCalculation(next.davis_bacon_schedule_job)) {
      calculationRunInProgressRef.current = false;
      setCalculationRunInProgress(false);
      setCurrentResultsState('failed');
      setNotice(null);
      setError(davisBaconCalculationFailureMessage(next.davis_bacon_schedule_job));
    }
    else setCurrentResultsState('idle');
  }, [publishRerunReminder]);

  const editDraft = useCallback((update: (current: EstimateDraft) => EstimateDraft) => {
    if (saveFailedRef.current) {
      saveQueueRef.current = Promise.resolve(acceptedDraftRef.current ?? partialDraftBaselineRef.current);
      saveFailedRef.current = false;
    }
    const previous = draftRef.current;
    const next = update(previous);
    const impact = rerunImpact(previous, next);
    if (impact && resultRef.current) {
      const current = rerunReminderRef.current;
      if (!current || (current.level === 'prices' && impact === 'materials')) {
        const settings = normalizeApproximateSettings(next.approximate_cost_plan_settings);
        const hasSelectedComponents = Boolean(settings && (settings.material_components.length
          + settings.assembly_components.length + settings.project_work_requirements.length));
        setRerunToast(rerunReminderMessage(impact, hasSelectedComponents));
      }
      publishRerunReminder({
        revision: resultRef.current.revision,
        level: impact === 'materials' || current?.level === 'materials' ? 'materials' : 'prices',
        ...(impact === 'materials' || current?.level === 'materials'
          ? { labor_plan_provenance: current?.level === 'materials'
            ? current.labor_plan_provenance ?? null
            : normalizeApproximateSettings(previous.approximate_cost_plan_settings)?.labor_plan_provenance ?? null }
          : {}),
      });
    }
    draftRef.current = next;
    editSequenceRef.current += 1;
    setDraft(next);
  }, [publishRerunReminder]);

  const reload = useCallback(async (
    respectLoadingPaintFloor = false, acceptCompletedPrefillDraft = false,
  ) => {
    if (activeFlowIdRef.current !== flowId) return null;
    const flowGeneration = flowGenerationRef.current;
    const reloadSequence = ++reloadSequenceRef.current;
    const editSequenceAtStart = editSequenceRef.current;
    const acceptedSaveAtStart = acceptedSaveSequenceRef.current;
    setError(null);
    try {
      const next = await getConstructionEstimate(flowId);
      if (respectLoadingPaintFloor && calculationStartedAtRef.current > 0) {
        const remaining = 300 - (Date.now() - calculationStartedAtRef.current);
        if (remaining > 0) await new Promise((resolve) => window.setTimeout(resolve, remaining));
      }
      if (flowGeneration !== flowGenerationRef.current
        || reloadSequence !== reloadSequenceRef.current) return null;
      const preserveLocalDraft = editSequenceRef.current !== editSequenceAtStart
        || acceptedSaveSequenceRef.current !== acceptedSaveAtStart
        || pendingSaveCountRef.current > 0
        || (editSequenceRef.current > 0
          && draftContent(draftRef.current) !== acceptedPayloadRef.current);
      applyLoad(next, preserveLocalDraft, acceptCompletedPrefillDraft);
      return next;
    } catch (caught) {
      if (flowGeneration !== flowGenerationRef.current
        || reloadSequence !== reloadSequenceRef.current) return null;
      setError(safeActionError(caught, 'The price estimate could not be loaded.'));
      return null;
    } finally {
      if (flowGeneration === flowGenerationRef.current
        && reloadSequence === reloadSequenceRef.current) setLoading(false);
    }
  }, [applyLoad, draftContent, flowId]);

  const requestSourceMatchProposal = useCallback((accepted: EstimateLoadData) => {
    const generation = flowGenerationRef.current;
    return requestConstructionSourceMatchProposal({
      flowId, accepted, fence: sourceMatchProposalFenceRef, reload,
      isCurrent: () => activeFlowIdRef.current === flowId && generation === flowGenerationRef.current,
    });
  }, [flowId, reload]);

  const settlePrefillJob = useCallback(async (next: PriceSearchPrefillJob) => {
    const flowGeneration = flowGenerationRef.current;
    const requestGeneration = prefillRequestGenerationRef.current;
    setPrefillJob(next);
    if (next.status === 'completed') {
      // A fast local/background worker can finish between the POST and its
      // response. Do not wait for a poll that will never be scheduled for a
      // terminal job: reload the server-owned draft immediately so proposed
      // location and other Contract Details replace the old editor values.
      const accepted = await reload(false, true);
      if (flowGeneration !== flowGenerationRef.current
        || requestGeneration !== prefillRequestGenerationRef.current) return;
      // Keep the spinner in place until the reload has applied the accepted
      // server draft. Otherwise a stale editor can briefly reappear between
      // terminal-job detection and the new location/details rendering.
      prefillPendingRef.current = false;
      setPreparingPrefill(false);
      if (!accepted) {
        setNotice('Contract filling completed, but its details could not be loaded. Try filling again.');
        return;
      }
      await requestSourceMatchProposal(accepted);
      if (flowGeneration !== flowGenerationRef.current
        || requestGeneration !== prefillRequestGenerationRef.current) return;
      setNotice(next.draft
        ? 'Contract details filled. Review and edit them before calculating prices.'
        : accepted.prefill?.outcome === 'proposed' || partialPrefillDraft(accepted.prefill)
          ? 'Some contract details were filled. Complete the missing project facts before calculating prices.'
          : 'No usable contract details were found. Enter project facts before calculating prices.');
      return;
    }
    prefillPendingRef.current = false;
    setPreparingPrefill(false);
    if (next.status === 'stale') {
      await reload();
      if (flowGeneration !== flowGenerationRef.current
        || requestGeneration !== prefillRequestGenerationRef.current) return;
      setError('The saved Construction Characteristics changed while details were being prepared. Refresh and try again.');
    } else if (next.status === 'failed') {
      setError('Contract details could not be filled. Your saved work is unchanged.');
    } else if (next.status === 'cancelled') {
      setNotice('Filling contract details was cancelled.');
    }
  }, [reload, requestSourceMatchProposal]);

  useEffect(() => {
    setLoading(true);
    setLoad(null);
    setRevision(null);
    setJob(null);
    setCalculating(false);
    setCancelling(false);
    setFinalizing(false);
    downloadInProgressRef.current = false;
    setCancellingPrefill(false);
    setSaveStatus('idle');
    setSourceMatchDecisions({});
    setSaving(false);
    setReviewingSourceMatches(false);
    setLaborPlanPreview(null);
    setLaborPlanProposal(null);
    setLaborPlanJob(null);
    setLaborPlanReviewRoles(null);
    setStartingLaborPlan(false);
    setApplyingLaborPlan(false);
    setScopeDocumentBusy(false);
    const reminder = readRerunReminder(flowId);
    rerunReminderRef.current = reminder;
    setRerunReminder(reminder);
    setRerunToast(null);
    setSubmittingDavisBaconReview(false);
    setRomAcknowledged(false);
    setAcknowledgedKeys(new Set());
    setComparableRationales({});
    void reload();
  }, [reload]);

  useEffect(() => {
    if (isViewOnly || !load?.draft?.approximate_cost_plan_settings) { setLaborPlanPreview(null); return undefined; }
    const isCurrent = captureFlowFence();
    let active = true;
    void Promise.resolve().then(() => getConstructionLaborPlanPreview(flowId)).then((preview) => {
      if (active && isCurrent()) setLaborPlanPreview(preview ?? null);
    }).catch(() => { if (active && isCurrent()) setLaborPlanPreview(null); });
    return () => { active = false; };
  }, [flowId, load?.draft?.draft_revision, Boolean(load?.draft?.approximate_cost_plan_settings), isViewOnly]);

  useEffect(() => {
    setLaborPlanReviewRoles(laborPlanProposal?.status === 'proposed'
      ? laborPlanProposal.site_staffing_roles.map((role) => ({ ...role })) : null);
  }, [laborPlanProposal?.proposal_id]);

  useEffect(() => {
    if (isViewOnly || !laborPlanJob || !['pending', 'running'].includes(laborPlanJob.status)) return undefined;
    const flowGeneration = flowGenerationRef.current;
    const requestGeneration = laborRequestGenerationRef.current;
    const timer = window.setTimeout(async () => {
      try {
        const next = await getConstructionLaborPlanJob(flowId, laborPlanJob.job_id);
        if (flowGeneration !== flowGenerationRef.current
          || requestGeneration !== laborRequestGenerationRef.current) return;
        setLaborPlanJob(next);
        if (next.status === 'completed') {
          if (next.proposal) setLaborPlanProposal(next.proposal);
          else await reload();
        } else if (next.status === 'failed' || next.status === 'stale') {
          setError(safeActionError(next.failure_reason, 'The labor plan could not be filled. Your saved project details are unchanged.'));
        }
      } catch (_caught) {
        if (flowGeneration === flowGenerationRef.current
          && requestGeneration === laborRequestGenerationRef.current) {
          setLaborPlanPollAttempt((attempt) => attempt + 1);
        }
      }
    }, pollIntervalMs);
    return () => window.clearTimeout(timer);
  }, [flowId, laborPlanJob, laborPlanPollAttempt, pollIntervalMs, reload, isViewOnly]);

  useEffect(() => {
    if (isViewOnly || !job || !ACTIVE_JOB_STATUSES.has(job.status)) return undefined;
    const flowGeneration = flowGenerationRef.current;
    const requestGeneration = resultRequestGenerationRef.current;
    const timer = window.setTimeout(async () => {
      try {
        const next = await getConstructionEstimateJob(flowId, job.job_id);
        if (flowGeneration !== flowGenerationRef.current
          || requestGeneration !== resultRequestGenerationRef.current) return;
        setJob(next);
        if (next.status === 'complete' || next.status === 'completed') {
          setCalculationStage('preparing');
          const loaded = await reload(true);
          if (flowGeneration !== flowGenerationRef.current
            || requestGeneration !== resultRequestGenerationRef.current) return;
          if (!loaded) setCurrentResultsState('unavailable');
        } else if (ACTIVE_JOB_STATUSES.has(next.status)) {
          setCalculationStage('searching');
        }
        if (next.status === 'canceled' || next.status === 'cancelled') {
          setCurrentResultsState('cancelled');
          setNotice(null);
        } else if (next.status === 'failed' || next.status === 'stale' || next.status === 'blocked') {
          const refreshed = await reload(true);
          if (flowGeneration !== flowGenerationRef.current
            || requestGeneration !== resultRequestGenerationRef.current) return;
          if (calculationRunInProgressRef.current && refreshed?.davis_bacon_schedule_job
            && ACTIVE_JOB_STATUSES.has(refreshed.davis_bacon_schedule_job.status)) {
            setCurrentResultsState('running');
            setCalculationStage('searching');
            return;
          }
          setCurrentResultsState('failed');
          setError(safeActionError(next.message, 'The estimate calculation stopped before completion. Your saved draft is unchanged.'));
        }
      } catch (caught) {
        if (flowGeneration !== flowGenerationRef.current
          || requestGeneration !== resultRequestGenerationRef.current) return;
        setCalculationStage('reconnecting');
        setPollAttempt((attempt) => attempt + 1);
      }
    }, pollIntervalMs);
    return () => window.clearTimeout(timer);
  }, [flowId, job, pollAttempt, pollIntervalMs, reload, isViewOnly]);

  const davisBaconScheduleJobIsActive = Boolean(load?.davis_bacon_schedule_job
    && ACTIVE_JOB_STATUSES.has(load.davis_bacon_schedule_job.status));
  useEffect(() => {
    if (isViewOnly || !davisBaconScheduleJobIsActive || (!revision && !calculationRunInProgress)
      || (job && ACTIVE_JOB_STATUSES.has(job.status))) return undefined;
    const flowGeneration = flowGenerationRef.current;
    const timer = window.setTimeout(() => {
      if (flowGeneration !== flowGenerationRef.current) return;
      void reload().finally(() => {
        if (flowGeneration === flowGenerationRef.current) {
          setWagePollAttempt((attempt) => attempt + 1);
        }
      });
    }, pollIntervalMs);
    return () => window.clearTimeout(timer);
  }, [calculationRunInProgress, davisBaconScheduleJobIsActive, job, pollIntervalMs, reload, revision, wagePollAttempt, isViewOnly]);

  useEffect(() => {
    if (!calculationRunInProgress || currentResultsState === 'running'
      || (job && ACTIVE_JOB_STATUSES.has(job.status)) || davisBaconScheduleJobIsActive) return;
    calculationRunInProgressRef.current = false;
    setCalculationRunInProgress(false);
  }, [calculationRunInProgress, currentResultsState, davisBaconScheduleJobIsActive, job]);

  useEffect(() => {
    if (isViewOnly || !prefillJob || !['pending', 'running'].includes(prefillJob.status)) return undefined;
    const flowGeneration = flowGenerationRef.current;
    const requestGeneration = prefillRequestGenerationRef.current;
    const timer = window.setTimeout(async () => {
      try {
        const next = await getConstructionPriceSearchPrefillJob(flowId, prefillJob.job_id);
        if (flowGeneration !== flowGenerationRef.current
          || requestGeneration !== prefillRequestGenerationRef.current) return;
        setPrefillJob(next);
        if (next.status === 'completed') {
          await settlePrefillJob(next);
        } else if (['stale', 'failed', 'cancelled'].includes(next.status)) {
          await settlePrefillJob(next);
        }
      } catch (_caught) {
        if (flowGeneration !== flowGenerationRef.current
          || requestGeneration !== prefillRequestGenerationRef.current) return;
        setPrefillPollAttempt((attempt) => attempt + 1);
      }
    }, pollIntervalMs);
    return () => window.clearTimeout(timer);
  }, [flowId, pollIntervalMs, prefillJob, prefillPollAttempt, settlePrefillJob, isViewOnly]);

  const capabilities = load?.capabilities;
  const primaryCostDataStatus = costDataPrimaryStatus(capabilities);
  const primaryCostDataConfigured = costDataPrimaryConfigured(primaryCostDataStatus);
  const primaryCostDataAvailability = costDataAvailabilityMessage(primaryCostDataStatus);
  const canEdit = !isViewOnly && capabilities?.can_edit !== false && capabilities?.enabled === true;
  const davisBaconCapability = capabilities?.davis_bacon_schedule;
  const davisBaconSchedule = load?.davis_bacon_schedule ?? null;

  useEffect(() => {
    // Details are fetched only for the collapsed evidence row in a completed
    // ROM. Before calculation, Wage Determination work is server-owned by
    // Calculate prices and has no standalone panel or controls.
    if (!revision || !canEdit || davisBaconCapability?.can_view_details !== true || !davisBaconSchedule?.schedule_id) {
      setDavisBaconDetails(null);
      setLoadingDavisBaconDetails(false);
      return;
    }
    let active = true;
    setLoadingDavisBaconDetails(true);
    void getConstructionDavisBaconScheduleDetails(flowId).then((details) => {
      if (active) setDavisBaconDetails(details);
    }).catch(() => {
      if (active) setError('Wage determination details could not be loaded. The determination summary remains available.');
    }).finally(() => {
      if (active) setLoadingDavisBaconDetails(false);
    });
    return () => { active = false; };
  }, [canEdit, davisBaconCapability?.can_view_details, davisBaconDetailsReload, davisBaconSchedule?.schedule_id, flowId, revision]);
  const blockers = load?.readiness?.blockers ?? [];
  const { isRom, romAckRequired, romAcknowledgementKey,
    requiredAcknowledgements, romFinalizationAllowed, canExport: canExportRevision } =
    constructionResultPermissions({ draft, revision, capabilities, isViewOnly,
      acknowledgedKeys, romAcknowledged });

  // Match the ordinary download's saved-draft branch without authoring or
  // bypassing finalization of a calculated revision that still matches it.
  const savedDraft = load?.draft;
  const revisionMatchesSavedDraft = Boolean(revision && savedDraft
    && revision.draft_revision === savedDraft.draft_revision
    && (!revision.draft_content_hash || revision.draft_content_hash === savedDraft.content_hash));
  const canExportSavedDraft = Boolean(!canExportRevision && isViewOnly && !isSampleFlow && (readOnlyReason === 'similar_requirements' || readOnlyReason === 'super_admin_view')
    && savedDraft?.content_hash && capabilities?.export_available === true
    && capabilities?.can_export === true && !revisionMatchesSavedDraft);
  const canExport = canExportRevision || canExportSavedDraft;

  return {
    flowName, isRom, romAckRequired, romAcknowledgementKey, requiredAcknowledgements, romFinalizationAllowed, canExport, canExportRevision, canExportSavedDraft, tourRef,
    load, setLoad, projectBriefText, setProjectBriefText, scopeDocumentBusy, setScopeDocumentBusy, laborPlanPreview,
    laborPlanProposal, setLaborPlanProposal, laborPlanJob, setLaborPlanJob, setLaborPlanPollAttempt, laborPlanReviewRoles, setLaborPlanReviewRoles, useSavedLaborPlan,
    setUseSavedLaborPlan, startingLaborPlan, setStartingLaborPlan, applyingLaborPlan, setApplyingLaborPlan, hasIncompleteCostComponentInputs, setHasIncompleteCostComponentInputs,
    rerunReminder, rerunToast, setRerunToast, draft, setDraft, revision, setRevision,
    job, setJob, loading, setLoading, saving, setSaving, calculating,
    setCalculating, preparingPrefill, setPreparingPrefill, prefillJob, setPrefillJob, setPrefillPollAttempt, cancellingPrefill,
    setCancellingPrefill, davisBaconDetails, setDavisBaconDetails, loadingDavisBaconDetails, setDavisBaconDetailsReload, davisBaconReviewReason, setDavisBaconReviewReason,
    submittingDavisBaconReview, setSubmittingDavisBaconReview, useSavedContractDetails, setUseSavedContractDetails, useSavedSourceResults, setUseSavedSourceResults, sourceMatchDecisions,
    setSourceMatchDecisions, reviewingSourceMatches, setReviewingSourceMatches, saveStatus, setSaveStatus, currentResultsState, setCurrentResultsState,
    calculationRunInProgress, setCalculationRunInProgress, calculationStage, setCalculationStage, setPollAttempt, finalizing, setFinalizing,
    error, setError, notice, setNotice, romAcknowledged, setRomAcknowledged, acknowledgedKeys,
    setAcknowledgedKeys, comparableRationales, setComparableRationales, cancelling, setCancelling, downloadInProgressRef, draftRef,
    acceptedDraftRef, partialDraftBaselineRef, acceptedPayloadRef, editSequenceRef, acceptedSaveSequenceRef, pendingSaveCountRef, saveQueueRef, saveFailedRef,
    calculationStartedAtRef, calculationRunInProgressRef, calculationHasTerminalWageEvidenceRef, prefillPendingRef, savedProjectBriefRef, projectBriefTextRef, flowGenerationRef,
    activeFlowIdRef, resultRequestGenerationRef, prefillRequestGenerationRef, laborRequestGenerationRef, scopeRequestGenerationRef, captureFlowFence, publishRerunReminder,
    applyLoad, editDraft, reload, settlePrefillJob, davisBaconScheduleJobIsActive, capabilities, primaryCostDataConfigured,
    primaryCostDataAvailability, canEdit, davisBaconCapability, davisBaconSchedule, blockers,
  };
}
